// DJSEM Prompt 93 — DJoid-inspired Graph Playlist & Track Relationship model (pure logic).
//
// This is the platform-agnostic core behind the TrackRelationshipCanvas / TransitionEdge UI. It
// runs identically on web and desktop (both mount the shared workspace). It computes node layout,
// edge relationships (harmonic + BPM + energy compatibility), toolbar operations (straighten,
// fit, collapse/expand), and per-edge transition risk — all deterministic and unit-testable with
// no canvas or DOM dependency. The React canvas layer is a thin renderer over this model.

import { keysCompatible } from "./djoidLibraryModel";
import type { DjoidTrackRow } from "./djoidLibraryModel";

/** A laid-out track node in the relationship graph canvas. */
export interface GraphNode {
  id: string;
  track: DjoidTrackRow;
  x: number;
  y: number;
  collapsed: boolean;
}

/** Overall mix-transition risk between two adjacent tracks. */
export type TransitionRisk = "smooth" | "caution" | "risky";

/** A directed transition edge between two tracks, with its compatibility score and risk. */
export interface GraphEdge {
  fromId: string;
  toId: string;
  /** harmonic + tempo + energy compatibility, 0..1 (higher = smoother). */
  compatibility: number;
  risk: TransitionRisk;
  reasons: string[];
}

/** A full graph: laid-out nodes plus their transition edges. */
export interface GraphLayout {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

const NODE_W = 220;
const NODE_H = 120;
const GAP_X = 80;
const GAP_Y = 60;

/**
 * Lay out tracks in reading order as a grid (deterministic; the UI can drag afterwards).
 * @param tracks - Tracks to place, in order.
 * @param columns - Grid column count; defaults to 3.
 * @returns One {@link GraphNode} per track.
 */
export function layoutGraph(tracks: DjoidTrackRow[], columns = 3): GraphNode[] {
  const cols = Math.max(1, columns);
  return tracks.map((track, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    return {
      id: track.id,
      track,
      x: col * (NODE_W + GAP_X),
      y: row * (NODE_H + GAP_Y),
      collapsed: false,
    };
  });
}

/**
 * "Straighten": snap every node back to the deterministic grid (toolbar action).
 * @param nodes - Current nodes (their `collapsed` flag is preserved).
 * @param columns - Grid column count; defaults to 3.
 * @returns Nodes re-laid-out on the grid.
 */
export function straighten(nodes: GraphNode[], columns = 3): GraphNode[] {
  return layoutGraph(nodes.map((n) => n.track), columns).map((laid, i) => ({
    ...laid,
    collapsed: nodes[i]?.collapsed ?? false,
  }));
}

/**
 * Set every node's collapsed flag (toolbar "collapse all" / "expand all").
 * @param nodes - Nodes to update.
 * @param collapsed - The collapsed value to apply to all nodes.
 * @returns A new array with every node's `collapsed` set to `collapsed`.
 */
export function collapseAll(nodes: GraphNode[], collapsed: boolean): GraphNode[] {
  return nodes.map((n) => ({ ...n, collapsed }));
}

/**
 * Score a directed transition between two tracks: harmonic, tempo and energy agreement.
 * @param from - The outgoing track.
 * @param to - The incoming track.
 * @returns A {@link GraphEdge} with compatibility score, risk band, and human-readable reasons.
 */
export function scoreTransition(from: DjoidTrackRow, to: DjoidTrackRow): GraphEdge {
  const reasons: string[] = [];
  let score = 1;

  // Harmonic (Camelot). Incompatible keys are the single biggest clash.
  const harmonic = from.camelot && to.camelot ? keysCompatible(from.camelot, to.camelot) : true;
  if (from.camelot && to.camelot && !harmonic) {
    score -= 0.5;
    reasons.push(`key clash ${from.camelot}→${to.camelot}`);
  }

  // Tempo. >6% BPM jump is a hard mix; 3–6% is a caution.
  if (typeof from.bpm === "number" && typeof to.bpm === "number" && from.bpm > 0) {
    const pct = Math.abs(to.bpm - from.bpm) / from.bpm;
    if (pct > 0.06) { score -= 0.35; reasons.push(`tempo jump ${(pct * 100).toFixed(0)}%`); }
    else if (pct > 0.03) { score -= 0.15; reasons.push(`tempo shift ${(pct * 100).toFixed(0)}%`); }
  }

  // Energy. A big drop mid-set breaks momentum; a big jump can jar.
  if (typeof from.energy === "number" && typeof to.energy === "number") {
    const d = to.energy - from.energy;
    if (d < -0.3) { score -= 0.2; reasons.push("energy drop"); }
    else if (d > 0.4) { score -= 0.1; reasons.push("energy spike"); }
  }

  const compatibility = Math.max(0, Math.min(1, score));
  const risk: TransitionRisk = compatibility >= 0.75 ? "smooth" : compatibility >= 0.45 ? "caution" : "risky";
  return { fromId: from.id, toId: to.id, compatibility, risk, reasons };
}

/**
 * Build the sequential edge chain for an ordered set (each track → the next).
 * @param tracks - Ordered tracks.
 * @returns One {@link GraphEdge} per adjacent pair.
 */
export function buildSequentialEdges(tracks: DjoidTrackRow[]): GraphEdge[] {
  const edges: GraphEdge[] = [];
  for (let i = 0; i < tracks.length - 1; i++) {
    edges.push(scoreTransition(tracks[i], tracks[i + 1]));
  }
  return edges;
}

/**
 * Full layout for an ordered set: grid nodes + sequential transition edges.
 * @param tracks - Ordered tracks.
 * @param columns - Grid column count; defaults to 3.
 * @returns A {@link GraphLayout} combining {@link layoutGraph} and {@link buildSequentialEdges}.
 */
export function buildGraphLayout(tracks: DjoidTrackRow[], columns = 3): GraphLayout {
  return { nodes: layoutGraph(tracks, columns), edges: buildSequentialEdges(tracks) };
}

/**
 * Fit-to-screen: the bounding box of all nodes plus the scale to fit a viewport.
 * @param nodes - Nodes to fit.
 * @param viewportW - Viewport width in px.
 * @param viewportH - Viewport height in px.
 * @param pad - Padding around the bounding box in px; defaults to 40.
 * @returns The scale and offset to apply, plus the bounding box's width/height.
 */
export function fitToScreen(nodes: GraphNode[], viewportW: number, viewportH: number, pad = 40): {
  scale: number; offsetX: number; offsetY: number; width: number; height: number;
} {
  if (nodes.length === 0) return { scale: 1, offsetX: 0, offsetY: 0, width: 0, height: 0 };
  const minX = Math.min(...nodes.map((n) => n.x));
  const minY = Math.min(...nodes.map((n) => n.y));
  const maxX = Math.max(...nodes.map((n) => n.x + NODE_W));
  const maxY = Math.max(...nodes.map((n) => n.y + NODE_H));
  const width = maxX - minX;
  const height = maxY - minY;
  const scale = Math.min(1, (viewportW - pad * 2) / width, (viewportH - pad * 2) / height);
  return { scale, offsetX: pad - minX * scale, offsetY: pad - minY * scale, width, height };
}

/**
 * Count how many edges are risky (for the toolbar/inspector summary).
 * @param edges - Edges to summarize.
 * @returns Counts of edges per {@link TransitionRisk} band.
 */
export function riskSummary(edges: GraphEdge[]): { smooth: number; caution: number; risky: number } {
  return edges.reduce(
    (acc, e) => { acc[e.risk] += 1; return acc; },
    { smooth: 0, caution: 0, risky: 0 } as { smooth: number; caution: number; risky: number },
  );
}

/** The fixed pixel dimensions used for every graph node (matches {@link layoutGraph}'s grid math). */
export const GRAPH_NODE_SIZE = { width: NODE_W, height: NODE_H } as const;
